import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid } from '@mui/x-data-grid';
import { Card, Grid, InputAdornment, MenuItem, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import { categoryIndentSx, flattenCategoryTree } from '../../../utils/categoryTree';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });

// Lista de solo consulta con los precios de venta del rol distribuidor por producto (para
// editarlos se sigue usando el formulario de producto en /admin/productos).
export default function DistributorPricesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [categoriesRes, brandsRes] = await Promise.all([productsApi.getCategories(), productsApi.getBrands()]);
        setCategories(categoriesRes.data.data);
        setBrands(brandsRes.data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar las categorías y marcas.'), {
          variant: 'error',
        });
      }
    };

    loadFilters();
  }, [enqueueSnackbar]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await productsApi.getProducts({
        search: search || undefined,
        category_id: categoryFilter || undefined,
        brand_id: brandFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de productos.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, brandFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const columns = useMemo(
    () => [
      { field: 'sku', headerName: 'SKU', width: 140 },
      { field: 'name', headerName: 'Nombre', flex: 1, minWidth: isMobile ? 140 : 220 },
      {
        field: 'category',
        headerName: 'Categoría',
        width: 160,
        valueGetter: (value, row) => row.category?.name || '—',
      },
      {
        field: 'brand',
        headerName: 'Marca',
        width: 140,
        valueGetter: (value, row) => row.brand?.name || '—',
      },
      {
        field: 'price_pen_distribuidor',
        headerName: 'Precio distribuidor (S/)',
        width: 180,
        valueFormatter: (value) => (value != null ? currencyPen.format(value) : '—'),
      },
    ],
    [isMobile]
  );

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Precios distribuidor</Typography>
        <Typography variant="body2" color="text.secondary">
          Precios de venta del rol distribuidor por producto, en soles con el tipo de cambio distribuidor de
          Configuración. Para editarlos, usa el formulario de producto en Productos.
        </Typography>
      </div>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nombre, SKU, código de barras o serie"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Categoría"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {flattenCategoryTree(categories).map((category) => (
                <MenuItem key={category.id} value={category.id} sx={categoryIndentSx(category.depth)}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Marca"
              value={brandFilter}
              onChange={(e) => {
                setBrandFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ width: '100%', overflowX: 'auto' }}>
        <div className={'dashTblResp'}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      </Card>
    </Stack>
  );
}
